import { useState } from "react";
import { submitForm } from "@/lib/submitForm";


export default function FoodInquiry() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validate textarea length
    const details = formData.get("project_details") as string;
    if (details && details.length > 500) {
      alert("Project details must be 500 characters or fewer.");
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value as string);
    });

    setStatus("sending");
    try {
      const res = await submitForm("food-processing", body);
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="inquiry" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left info */}
          <div>
            <span className="text-teal-600 text-xs font-bold uppercase tracking-widest">Get in Touch</span>
            <h2 className="text-gray-900 text-3xl md:text-4xl font-extrabold mt-3 uppercase tracking-tight leading-tight">
              Request a Free Quote
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mt-5">
              Tell us about your food processing project and one of our engineers will
              reach out within 24 hours to discuss your requirements, site specifics,
              and provide a no-obligation estimate.
            </p>

            {/* Contact cards */}
            <div className="space-y-5 mt-10">
              {[
                { icon: "ri-phone-line", label: "Call Us", value: "+254 707 296 060" },
                { icon: "ri-mail-line", label: "Email Us", value: "info@isman.co.ke" },
                { icon: "ri-map-pin-2-line", label: "Workshop", value: "Nairobi, Kenya" },
              ].map((c) => (
                <div key={c.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg flex-shrink-0">
                    <i className={`${c.icon} text-lg text-teal-600`}></i>
                  </div>
                  <div>
                    <p className="text-gray-900 font-bold text-xs uppercase tracking-wide">{c.label}</p>
                    <p className="text-gray-500 text-sm mt-0.5">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Info image */}
            <div className="mt-10 w-full h-52 rounded-lg overflow-hidden">
              <img
                src="/images/food/conveyor.webp"
                alt="ISMAN Engineering Consultation"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          {/* Form */}
          <div className="bg-gray-50 rounded-lg p-8 border border-gray-100">
            {status === "success" ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-5">
                  <i className="ri-check-double-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-gray-900 font-extrabold text-lg uppercase tracking-tight">Enquiry Sent!</h3>
                <p className="text-gray-500 text-sm mt-3 leading-relaxed">
                  Thank you — one of our engineers will contact you within 24 hours to discuss your project.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="mt-6 text-teal-600 text-xs font-semibold uppercase tracking-wide hover:underline cursor-pointer"
                >
                  Submit Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-isman-form id="food-processing-inquiry">
                <h3 className="text-gray-900 font-extrabold text-base uppercase tracking-tight mb-6">
                  Project Details
                </h3>

                <div className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Full Name <span className="text-teal-600">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      placeholder="e.g. John Kamau"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Email Address <span className="text-teal-600">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@company.com"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="+254 7XX XXX XXX"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  {/* Industry type */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Industry / Sector <span className="text-teal-600">*</span>
                    </label>
                    <select
                      name="industry"
                      required
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-500 transition-colors"
                    >
                      <option value="">Select industry...</option>
                      <option>Dairy Processing</option>
                      <option>Beverage / Juice</option>
                      <option>Meat / Abattoir</option>
                      <option>Fish Processing</option>
                      <option>Bakery / Confectionery</option>
                      <option>Grain / Flour Milling</option>
                      <option>Fruits & Vegetables</option>
                      <option>Agro-Processing</option>
                      <option>Other</option>
                    </select>
                  </div>

                  {/* Equipment needed */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Equipment Required <span className="text-teal-600">*</span>
                    </label>
                    <select
                      name="equipment_required"
                      required
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-700 focus:outline-none focus:border-teal-500 transition-colors"
                    >
                      <option value="">Select equipment type...</option>
                      <option>Mixing &amp; Blending Tanks</option>
                      <option>Conveyor Systems</option>
                      <option>Processing &amp; Cutting Tables</option>
                      <option>Storage Silos &amp; Hoppers</option>
                      <option>Pasteurisation &amp; Heat Exchangers</option>
                      <option>Filling &amp; Packaging Stations</option>
                      <option>Multiple / Full Line</option>
                    </select>
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Site Location
                    </label>
                    <input
                      type="text"
                      name="site_location"
                      placeholder="e.g. Thika, Nairobi"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  {/* Project details */}
                  <div>
                    <label className="block text-gray-700 text-xs font-semibold uppercase tracking-wide mb-1">
                      Project Details
                    </label>
                    <textarea
                      name="project_details"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your requirements, production volumes, dimensions, timeline, etc. (max 500 characters)"
                      className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-teal-500 transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "sending"}
                    className="w-full bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white font-bold py-3 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {status === "sending" ? "Sending..." : "Submit Enquiry"}
                  </button>

                  {status === "error" && (
                    <p className="text-red-500 text-xs text-center">
                      Something went wrong. Please try again or call us directly.
                    </p>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
